import { Platform } from 'react-native';

// Configuration RevenueCat
export const REVENUECAT_CONFIG = {
  apiKeyIOS: 'appl_PPdkNTgPPSZtyBJuERrnZTcgQPB',
  apiKeyAndroid: 'goog_cYAWcuoUuuYIQnkUKXxDTtEUznr',
  entitlementId: 'Ernest Premium',
  offeringId: 'ernest_premium',
};

// Types pour RevenueCat
export interface RCPackage {
  identifier: string;
  packageType: string;
  product: {
    identifier: string;
    title: string;
    description: string;
    price: number;
    priceString: string;
    currencyCode: string;
  };
  offeringIdentifier: string;
}

export interface RCOffering {
  identifier: string;
  availablePackages: RCPackage[];
  monthly?: RCPackage;
  annual?: RCPackage;
}

export interface RCCustomerInfo {
  entitlements: {
    active: {
      [key: string]: {
        identifier: string;
        isActive: boolean;
        willRenew: boolean;
        expirationDate: string | null;
        productIdentifier: string;
      };
    };
  };
  activeSubscriptions: string[];
  originalAppUserId: string;
}

// Variable pour stocker l'instance Purchases
let Purchases: any = null;
let isConfigured = false;

/**
 * Initialise RevenueCat avec les clés API
 * À appeler au démarrage de l'app
 */
export const configureRevenueCat = async (userId?: string): Promise<boolean> => {
  try {
    // Import dynamique pour éviter les erreurs dans Expo Go
    const RNPurchases = require('react-native-purchases').default;
    Purchases = RNPurchases;

    const apiKey = Platform.OS === 'ios' 
      ? REVENUECAT_CONFIG.apiKeyIOS 
      : REVENUECAT_CONFIG.apiKeyAndroid;

    // Configuration avec ou sans userId
    if (userId) {
      await Purchases.configure({ apiKey, appUserID: userId });
    } else {
      await Purchases.configure({ apiKey });
    }

    isConfigured = true;
    console.log('✅ RevenueCat configured successfully');
    return true;
  } catch (error) {
    console.log('⚠️ RevenueCat not available (Expo Go or missing package):', error);
    isConfigured = false;
    return false;
  }
};

/**
 * Vérifie si RevenueCat est disponible et configuré
 */
export const isRevenueCatAvailable = (): boolean => {
  return isConfigured && Purchases !== null;
};

/**
 * Connecte un utilisateur à RevenueCat (après login)
 */
export const loginRevenueCat = async (userId: string): Promise<RCCustomerInfo | null> => {
  if (!isRevenueCatAvailable()) {
    console.log('⚠️ RevenueCat not available for login');
    return null;
  }

  try {
    const { customerInfo } = await Purchases.logIn(userId);
    console.log('✅ RevenueCat user logged in:', userId);
    return customerInfo;
  } catch (error) {
    console.error('❌ RevenueCat login error:', error);
    return null;
  }
};

/**
 * Déconnecte l'utilisateur de RevenueCat (après logout)
 */
export const logoutRevenueCat = async (): Promise<void> => {
  if (!isRevenueCatAvailable()) return;

  try {
    await Purchases.logOut();
    console.log('✅ RevenueCat user logged out');
  } catch (error) {
    console.error('❌ RevenueCat logout error:', error);
  }
};

/**
 * Récupère les offerings (produits disponibles)
 */
export const getOfferings = async (): Promise<RCOffering | null> => {
  if (!isRevenueCatAvailable()) {
    console.log('⚠️ RevenueCat not available for offerings');
    return null;
  }

  try {
    const offerings = await Purchases.getOfferings();
    
    if (offerings.current) {
      console.log('✅ RevenueCat offerings loaded:', offerings.current.identifier);
      return offerings.current;
    }
    
    // Fallback sur l'offering spécifique
    if (offerings.all[REVENUECAT_CONFIG.offeringId]) {
      return offerings.all[REVENUECAT_CONFIG.offeringId];
    }

    console.log('⚠️ No offerings available');
    return null;
  } catch (error) {
    console.error('❌ Error fetching offerings:', error);
    return null;
  }
};

/**
 * Effectue un achat
 */
export const purchasePackage = async (rcPackage: RCPackage): Promise<{
  success: boolean;
  customerInfo?: RCCustomerInfo;
  error?: string;
}> => {
  if (!isRevenueCatAvailable()) {
    return { success: false, error: 'RevenueCat not available' };
  }

  try {
    const { customerInfo } = await Purchases.purchasePackage(rcPackage);
    
    // Vérifier si l'utilisateur a maintenant l'entitlement
    const isPremium = customerInfo.entitlements.active[REVENUECAT_CONFIG.entitlementId]?.isActive;
    
    if (isPremium) {
      console.log('✅ Purchase successful!');
      return { success: true, customerInfo };
    } else {
      return { success: false, error: 'Purchase completed but entitlement not active' };
    }
  } catch (error: any) {
    // Gérer l'annulation par l'utilisateur
    if (error.userCancelled) {
      console.log('ℹ️ User cancelled purchase');
      return { success: false, error: 'cancelled' };
    }
    
    console.error('❌ Purchase error:', error);
    return { success: false, error: error.message || 'Purchase failed' };
  }
};

/**
 * Récupère les informations du client (abonnements actifs)
 */
export const getCustomerInfo = async (): Promise<RCCustomerInfo | null> => {
  if (!isRevenueCatAvailable()) {
    return null;
  }

  try {
    const customerInfo = await Purchases.getCustomerInfo();
    return customerInfo;
  } catch (error) {
    console.error('❌ Error getting customer info:', error);
    return null;
  }
};

/**
 * Vérifie si l'utilisateur a un abonnement premium actif via RevenueCat
 */
export const checkPremiumStatus = async (): Promise<{
  isPremium: boolean;
  expirationDate?: string;
  willRenew?: boolean;
  productId?: string;
}> => {
  const customerInfo = await getCustomerInfo();
  
  if (!customerInfo) {
    return { isPremium: false };
  }

  const entitlement = customerInfo.entitlements.active[REVENUECAT_CONFIG.entitlementId];
  
  if (entitlement?.isActive) {
    return {
      isPremium: true,
      expirationDate: entitlement.expirationDate || undefined,
      willRenew: entitlement.willRenew,
      productId: entitlement.productIdentifier,
    };
  }

  return { isPremium: false };
};

/**
 * Restaure les achats précédents
 */
export const restorePurchases = async (): Promise<{
  success: boolean;
  isPremium: boolean;
  error?: string;
}> => {
  if (!isRevenueCatAvailable()) {
    return { success: false, isPremium: false, error: 'RevenueCat not available' };
  }

  try {
    const customerInfo = await Purchases.restorePurchases();
    const isPremium = customerInfo.entitlements.active[REVENUECAT_CONFIG.entitlementId]?.isActive || false;
    
    console.log('✅ Purchases restored, premium:', isPremium);
    return { success: true, isPremium };
  } catch (error: any) {
    console.error('❌ Restore error:', error);
    return { success: false, isPremium: false, error: error.message };
  }
};

/**
 * Écoute les changements d'abonnement en temps réel
 */
export const addCustomerInfoUpdateListener = (
  callback: (customerInfo: RCCustomerInfo) => void
): (() => void) => {
  if (!isRevenueCatAvailable()) {
    return () => {}; // No-op
  }

  const listener = Purchases.addCustomerInfoUpdateListener(callback);
  return () => listener.remove();
};
